<template>
  <div v-if="active && items.length > 1" class="relative">
    <FadeInTransition>
      <BasicSliderArrow
        v-if="shouldShowArrows"
        side="left"
        :small="small"
        :outside="arrowsOutside"
        @click="handlePrevClick"
      />
    </FadeInTransition>
    <FadeInTransition>
      <BasicSliderArrow
        v-if="shouldShowArrows"
        side="right"
        :small="small"
        :outside="arrowsOutside"
        @click="handleNextClick"
      />
    </FadeInTransition>

    <div
      class="relative overflow-hidden"
      :class="{ 'hide-scrollbar': hideScrollbar }"
    >
      <Intersect :threshold="0.5" @enter.once="emitSlideVisible">
        <Swiper
          ref="swiperRef"
          :modules="[SwiperNavigation]"
          :slides-per-view="itemsPerSlide"
          :slides-per-group="itemsPerSlide"
          :navigation="false"
          :grab-cursor="true"
          :resistance="false"
          :allow-touch-move="true"
          :speed="600"
          :preload-images="false"
          :watch-slides-progress="true"
          :observer="true"
          :observe-parents="true"
          :center-insufficient-slides="centerContent"
          @swiper="onSwiper"
          @slide-change="emitSlideVisible"
        >
          <SwiperSlide
            v-for="(item, index) in items"
            :key="keyProp ? item[keyProp] : index"
            class="box-border flex-none snap-center"
            :style="getSlideStyle(index)"
          >
            <slot name="item" :item="item" :index="index">
              {{ item }}
            </slot>
          </SwiperSlide>
        </Swiper>
      </Intersect>
    </div>
  </div>
  <Intersect v-else :threshold="0.5" @enter.once="emitStaticVisible">
    <div
      class="flex"
      :class="{ 'justify-center': centerContent }"
      :style="{
        gap: `${pixelGap}px`,
      }"
    >
      <div
        v-for="(item, index) in items"
        :key="keyProp ? item[keyProp] : index"
        :style="itemStyles"
      >
        <slot name="item" :item="item" :index="index">
          {{ item }}
        </slot>
      </div>
    </div>
  </Intersect>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, nextTick, computed, ref, watch } from 'vue'
import type { PropType } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation as SwiperNavigation } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper/types'
import 'swiper/css'
import { useWindowSize } from '@vueuse/core'
import { useDeviceType } from '~/composables/useDeviceType'

const props = defineProps({
  items: { type: Array as PropType<any[]>, required: true },
  keyProp: { type: String, default: undefined },
  active: { type: Boolean, default: true },
  maxItemsPerSlide: { type: Number, default: 4 },
  maxItemsPerSlideMobile: { type: Number, default: 2 },
  small: { type: Boolean, default: false },
  pixelGap: { type: Number, default: 8 },
  arrowsOutside: { type: Boolean, default: false },
  hideScrollbar: { type: Boolean, default: true },
  centerContent: { type: Boolean, default: true },
})

const emit = defineEmits(['slideVisible'])

const { isMobile, isMd } = useDeviceType()
const swiper = ref<SwiperType>()

const itemsPerSlide = computed(() => {
  if (isMobile.value) {
    return props.maxItemsPerSlideMobile
  }
  if (isMd.value) {
    return props.maxItemsPerSlide - 2
  }
  return props.maxItemsPerSlide
})

const itemStyles = computed(() => ({
  width: `calc(${100 / itemsPerSlide.value}% - ${
    (props.pixelGap * (itemsPerSlide.value - 1)) / itemsPerSlide.value
  }px)`,
  flexShrink: 0,
}))

// ECOM-2225: render the inter-tile gap as a fixed `margin-right` on the slide
// element itself (with `box-border` + the shared `itemStyles` width), so every
// tile keeps an identical width. The last item of each page (and the very last
// item) drops the margin so each page fills exactly 100%.
//
// Why here and not the two prior approaches:
//  - Swiper's `space-between` is applied by Swiper's JS *after* hydration, which
//    shifts the slides on the client → CLS (the problem ECOM-807 set out to fix).
//  - ECOM-807 instead put this margin on an *inner* wrapper div, which shrank
//    each tile's content while the last tile kept full width → unequal tiles
//    (ECOM-2177), and it was reverted.
// Rendering the margin on the slide server-side keeps the layout identical
// before and after hydration (no CLS), keeps all tiles equal (fixed width,
// external margin), and Swiper still measures the slide's `margin-right` when
// positioning slides, so snapping stays correct.
const getSlideStyle = (index: number) => ({
  ...itemStyles.value,
  marginRight:
    (index + 1) % itemsPerSlide.value === 0 || index === props.items.length - 1
      ? '0px'
      : `${props.pixelGap}px`,
})

const shouldShowArrows = computed(() => {
  return props.items.length > itemsPerSlide.value
})

// activeIndex represents the position of the first item in the slide in the items array
const activeIndex = computed(() => swiper.value?.activeIndex || 0)

// items currently visible in the active slide
const visibleItems = computed(() => {
  const start = activeIndex.value
  const end = Math.min(start + itemsPerSlide.value, props.items.length)

  return props.items.slice(start, end)
})

// called only once when Swiper initializes
const onSwiper = (swiperInstance: SwiperType) => {
  swiper.value = swiperInstance
}

// used instead of handleVisibility functionality which is usually called by the intersection observer to override the emitted items and index
const emitSlideVisible = () => {
  const slideCount = Math.ceil(props.items.length / itemsPerSlide.value)
  const slideIndex =
    activeIndex.value !== 0
      ? Math.round(activeIndex.value / itemsPerSlide.value)
      : activeIndex.value

  const items = visibleItems.value.map((item, index) => ({
    ...item,
    index: slideIndex * itemsPerSlide.value + index,
  }))
  emit('slideVisible', items, slideCount, slideIndex)
}

// Single-slide / non-swiper render path (items.length <= 1). Emits a single
// impression when it scrolls into view, but only when the slider is active —
// `active=false` suppresses the list and must not fire an impression (ECOM-2618).
const emitStaticVisible = () => {
  if (!props.active) {
    return
  }
  emitSlideVisible()
}

const handleResize = () => {
  if (!swiper.value || swiper.value.destroyed) {
    return
  }
  requestAnimationFrame(() => {
    if (swiper.value && !swiper.value.destroyed) {
      swiper.value.update()
    }
  })
}

const { width } = useWindowSize()

let resizeDebounceTimer: ReturnType<typeof setTimeout> | undefined

watch(width, () => {
  if (resizeDebounceTimer) {
    clearTimeout(resizeDebounceTimer)
  }
  resizeDebounceTimer = setTimeout(() => {
    nextTick(handleResize)
  }, 500)
})

onMounted(() => {
  nextTick(handleResize)
})

const handlePrevClick = () => {
  if (!swiper.value || swiper.value.destroyed) {
    return
  }

  if (swiper.value.isBeginning) {
    const totalSlides = props.items.length
    const numGroups = Math.ceil(totalSlides / itemsPerSlide.value)
    const lastPosition = (numGroups - 1) * itemsPerSlide.value
    swiper.value.slideTo(lastPosition)
  } else {
    swiper.value.slidePrev()
  }
}

const handleNextClick = () => {
  if (!swiper.value || swiper.value.destroyed) {
    return
  }

  if (swiper.value.isEnd) {
    swiper.value.slideTo(0)
  } else {
    swiper.value.slideNext()
  }
}

onBeforeUnmount(() => {
  try {
    if (resizeDebounceTimer) {
      clearTimeout(resizeDebounceTimer)
    }
    if (swiper.value && !swiper.value.destroyed && swiper.value.el) {
      swiper.value.destroy(true, true)
    }
  } catch (e) {
    console.warn('Swiper destroy failed:', e)
  }
})
</script>

<style lang="scss" scoped>
:deep(.swiper) {
  overflow: visible;
  width: 100%;
}

:deep(.swiper-wrapper) {
  display: flex;
  width: 100%;
}

:deep(.swiper-slide) {
  height: auto;
  flex-shrink: 0;
  padding: 2px 0;
}

.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
